import { useState } from "react"
import { Link } from "react-router-dom"
import {
  ChevronDown,
  Lightbulb,
  Clock,
  ExternalLink,
  User,
  MapPin,
  Phone,
  Mail
} from "lucide-react"
import { DateTime } from "luxon"
import { cn } from "../../utils/classnames"
import { LeadScore } from "../../../types"
import ScoreCircle from "./score-circle"
import { CATEGORY_CONFIG, cleanSuggestedAction } from "./category-helpers"

interface Props {
  score: LeadScore
  index?: number
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value)

const LeadScoreCard = ({ score, index = 0 }: Props) => {
  const [expanded, setExpanded] = useState(false)
  const config = CATEGORY_CONFIG[score.category]
  const sub = score.subscription
  const lo = score.loan_officer
  const quote = sub?.quote_info

  const displayName =
    sub?.first_name && sub?.last_name
      ? `${sub.first_name} ${sub.last_name}`
      : sub?.email ?? ""

  const scoredAtTimestamp = Number(score.scored_at)
  const scoredAtRelative =
    scoredAtTimestamp && !isNaN(scoredAtTimestamp)
      ? DateTime.fromSeconds(scoredAtTimestamp).toRelative() ?? ""
      : ""

  const location = quote?.location
  const locationStr = [location?.city, location?.state]
    .filter(Boolean)
    .join(", ")

  return (
    <div
      className={cn(
        "bg-white rounded-lg border border-neutral-200 shadow-sm hover:shadow-md transition-shadow",
        "border-l-4",
        config.borderClass,
        "animate-fade-slide-in"
      )}
      style={{ animationDelay: `${index * 0.05}s`, animationFillMode: "both" }}
    >
      <div className="p-4 sm:p-5">
        {/* Top bar: time + view link */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1 text-xs text-neutral-400">
            <Clock size={12} />
            <span>{scoredAtRelative}</span>
          </div>
          <Link
            to={`/engagements/${score.subscription_id}`}
            className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-600 transition-colors"
          >
            View <ExternalLink size={12} />
          </Link>
        </div>

        {/* Identity row: score + name + badge */}
        <div className="flex items-start gap-3 sm:gap-4 mb-3">
          <ScoreCircle score={Number(score.intent_score)} index={index} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-base font-bold text-neutral-800">
                {displayName}
              </span>
              <span className={`badge ${config.badgeClass} whitespace-nowrap`}>
                {config.label}
              </span>
            </div>

            {/* Metadata: loan, location, LO */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-400">
              {quote?.loan_amount && (
                <span>
                  {formatCurrency(quote.loan_amount)}
                  {quote.loan_purpose && ` ${quote.loan_purpose}`}
                </span>
              )}
              {locationStr && (
                <span className="flex items-center gap-1">
                  <MapPin size={10} />
                  {locationStr}
                </span>
              )}
              {lo?.name && (
                <span className="flex items-center gap-1">
                  <User size={10} />
                  {lo.name}
                </span>
              )}
            </div>

            {/* Contact links */}
            {(sub?.phone || sub?.email) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs">
                {sub?.phone && (
                  <a
                    href={`tel:${sub.phone}`}
                    className="flex items-center gap-1 text-primary-400 hover:text-primary-600 transition-colors"
                    onClick={e => e.stopPropagation()}
                  >
                    <Phone size={10} />
                    {sub.phone}
                  </a>
                )}
                {sub?.email && (
                  <a
                    href={`mailto:${sub.email}`}
                    className="flex items-center gap-1 text-primary-400 hover:text-primary-600 transition-colors"
                    onClick={e => e.stopPropagation()}
                  >
                    <Mail size={10} />
                    {sub.email}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <p className="text-sm text-neutral-700 mb-3">{score.summary}</p>

        {/* Suggested action */}
        <div className="flex items-start gap-2 bg-teal-50 border-l-2 border-primary-400 pl-3 pr-3 py-2 rounded-r mb-3">
          <Lightbulb size={14} className="text-primary-500 shrink-0 mt-0.5" />
          <p className="text-xs text-neutral-700">
            {cleanSuggestedAction(score.suggested_action)}
          </p>
        </div>

        {/* Key signals */}
        {score.key_signals?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {score.key_signals.map((signal, i) => (
              <span
                key={i}
                className="bg-neutral-100 text-neutral-600 rounded-full px-2.5 py-0.5 text-xs"
              >
                {signal}
              </span>
            ))}
          </div>
        )}

        {/* Expand reasoning toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          <ChevronDown
            size={14}
            className={cn(
              "transition-transform duration-200",
              expanded && "rotate-180"
            )}
          />
          {expanded ? "Hide" : "View"} reasoning
        </button>

        {/* Expanded reasoning */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <p className="text-xs text-neutral-500 leading-relaxed">
              {score.reasoning}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default LeadScoreCard
